import type { ExpenseLine } from '../charges/expense-lines';

export enum ExpenseType {
    Syndic = "Syndic Invoice",
    Repair = "Repair",
    Maintenance = "Maintenance",
    Preventive = "Preventive Maintenance",
    SmallRepairs = "Small Repairs",
    Water = "Water",
    Gas = "Gas",
    Electricity = "Electricity",
    Precompte = "Précompte",
    Tax = "Tax",
    Insurance = "Insurance",
    Mortgage = "Mortgage",
    Legal = "Legal Fees",
    Management = "Management Fees",
    Gardening = "Gardening",
    Cleaning = "Cleaning",
    Furnishing = "Furnishing",
    Renovation = "Renovation",
    Other = "Other"
}

export enum ExpenseChargedTo {
    Owner = "Owner",
    Tenant = "Tenant"
}

/** Who actually paid a given instalment. Absent on a payment means the owner. */
export type ExpensePayerKind = 'self' | 'tenant' | 'other';

export interface ExpensePayer {
    kind: ExpensePayerKind;
    /** Free-text name, only meaningful for `other`. */
    name?: string;
}

/**
 * A single payment recorded against an expense. An expense may be settled in
 * several instalments — the sum of `payments[].amount` is compared to the
 * expense `amount` to derive the unpaid / partial / paid status.
 *
 * Additive model: legacy expenses carry no `payments` array and keep relying on
 * the binary `isPaid` + `paymentDate` fields, which we continue to maintain as
 * derived values so the ~50 existing consumers are unaffected.
 */
export interface ExpensePayment {
    /** Stable client-generated id (used as the FormArray / *ngFor track key). */
    id: string;
    amount: number;
    date: Date | string;
    /** Optional free-text label (e.g. "Acompte", bank reference). */
    note?: string;
    /** Who paid this instalment. Absent = the owner (every record created before this field). */
    paidBy?: ExpensePayer;
}

export class Expense {
    expenseUID?: string;
    propertyUID?: string;
    propertyName?: string;
    /** Optional free-text category captured in the add-expense dialog. */
    category?: string;
    buildingUID?: string;
    buildingName?: string;
    // Syndic context — optional unit within the building.
    unitUID?: string;
    unitName?: string;
    date!: Date | string;
    type!: ExpenseType;
    amount!: number;
    description?: string;
    chargedTo: ExpenseChargedTo = ExpenseChargedTo.Owner;
    tenantUID?: string;
    /**
     * Tenant's percentage (0-100) when the expense is shared with the owner.
     * Meaningful only with `chargedTo = Tenant`; absent means the tenant carries
     * all of it, which is what every record created before splits means.
     */
    tenantSharePercent?: number;
    isPaid = false;
    paymentDate?: Date | string;
    /**
     * Recorded instalments. When present and non-empty this is the source of
     * truth for how much has been settled; `isPaid` / `paymentDate` are then
     * derived from it (see `shared/utils/expense-payments.ts`).
     */
    payments?: ExpensePayment[];
    /** Lignes d'une facture d'immeuble (#680, facture mixte). Absent = une seule ligne implicite. */
    lines?: ExpenseLine[];
    /** Facture (#680, audit) : numéro, TVA, période couverte (ISO `YYYY-MM-DD`). */
    invoiceNumber?: string;
    /** TVA de la facture ; `amount` est alors le montant HTVA (dépense d'immeuble). */
    vatAmount?: number;
    coveredFrom?: string;
    coveredTo?: string;
    documentUIDs?: string[];
    documentURL?: string; // For single document upload backward compatibility
    ownerUID?: string;
    createdAt: Date = new Date();
    updatedAt: Date = new Date();

    // Cockpit provenance — stamped by the `addExpense` executor when this
    // expense was logged from an email-triggered workflow (utility bill,
    // syndic charge, repair invoice). Enables the "Issu du Cockpit" back-link.
    origin?: 'cockpit';
    cardId?: string;
    effectId?: string;

    // Meter link — optional, energy expenses only (electricity / gas / water).
    // The provider, contract and dates are NOT copied here: they are read from
    // the meter, the single source. Indices bound the consumption of the bill.
    // On edit, unlinking is written as `''` / `null` (never `undefined`): the
    // store strips `undefined`, which would leave the old link in Firestore.
    meterUID?: string;
    meterIndexStart?: number | null;
    meterIndexEnd?: number | null;

    // Supplier link — optional reference to a `service_providers` carnet entry,
    // plus the denormalized vendor identity. `vendorName`/`vendorEmail` are also
    // stamped by the cockpit `addExpense` executor when the expense is logged
    // from an email-triggered workflow; here they are filled from the picked
    // provider so a manually-logged expense carries the same human label.
    providerUID?: string;
    vendorName?: string;
    vendorEmail?: string;

    constructor(
        propertyUID: string | undefined,
        date: Date,
        type: ExpenseType,
        amount: number,
        description?: string,
        chargedTo: ExpenseChargedTo = ExpenseChargedTo.Owner,
        isPaid = false,
        paymentDate?: Date,
        documentUIDs?: string[],
        tenantUID?: string,
        buildingUID?: string
    ) {
        this.propertyUID = propertyUID;
        this.buildingUID = buildingUID;
        this.date = date;
        this.type = type;
        this.amount = amount;
        this.description = description;
        this.chargedTo = chargedTo;
        this.isPaid = isPaid;
        this.paymentDate = paymentDate;
        this.documentUIDs = documentUIDs;
        this.tenantUID = tenantUID;
    }
}